/**
 * CalendarOAuthDiagnosticsSheet
 *
 * Painel lateral (Sheet) com a timeline completa de eventos do fluxo OAuth
 * do Google Calendar — usado para diagnosticar falhas de conexão.
 *
 * - Agrupa eventos por correlation_id
 * - Mostra fase, status, latência, mensagens de erro e payloads sanitizados
 * - Filtros: somente falhas + busca por correlation_id
 * - Auto-refresh a cada 5s enquanto aberto
 * - Botão "Copiar diagnóstico" → JSON completo para suporte
 *
 * RELEASE:
 *   2026-06-24 v1.0.0 - Versão inicial.
 */

import { useState, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Clock,
  Copy,
  Loader2,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  useCalendarOAuthDiagnostics,
  groupByCorrelation,
  type CalendarOAuthEvent,
} from '@/hooks/useCalendarOAuthDiagnostics';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pré-filtra por correlation_id específico (vindo do toast de erro) */
  initialCorrelationId?: string | null;
}

const STATUS_STYLES: Record<string, string> = {
  success: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
  failed: 'bg-destructive/10 text-destructive border-destructive/30',
  pending: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  skipped: 'bg-muted text-muted-foreground border-border',
};

const PHASE_LABEL: Record<string, string> = {
  authorize: 'Autorização',
  callback: 'Callback',
  token_exchange: 'Troca de Token',
  userinfo: 'Buscar Email',
  tenant_resolution: 'Resolver Tenant',
  upsert: 'Salvar Integração',
  done: 'Concluído',
  error: 'Erro Global',
};

function StatusIcon({ status }: { status: string }) {
  if (status === 'success') return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === 'failed') return <XCircle className="h-4 w-4 text-destructive" />;
  if (status === 'pending') return <Loader2 className="h-4 w-4 text-amber-600 animate-spin" />;
  return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
}

function shortCid(cid: string) {
  return cid.slice(0, 8).toUpperCase();
}

function EventRow({ event }: { event: CalendarOAuthEvent }) {
  const [open, setOpen] = useState(event.processing_status === 'failed');

  const hasDetails =
    event.error_message ||
    event.metadata ||
    event.raw_response_snapshot ||
    event.change_diff ||
    event.error_stack;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex items-start gap-3 py-2.5 border-l-2 border-border pl-3 ml-1.5 relative">
        <div className="absolute -left-[7px] top-3.5 h-3 w-3 rounded-full bg-background border-2 border-border flex items-center justify-center">
          <StatusIcon status={event.processing_status} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <span className="text-sm font-medium">{event.event_type}</span>
            <Badge
              variant="outline"
              className={STATUS_STYLES[event.processing_status] || ''}
            >
              {event.processing_status}
            </Badge>
            <Badge variant="secondary" className="text-xs">
              {PHASE_LABEL[event.phase] || event.phase}
            </Badge>
            {event.latency_ms != null && (
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {event.latency_ms}ms
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {format(new Date(event.created_at), "dd/MM 'às' HH:mm:ss.SSS", { locale: ptBR })}
          </p>
          {event.error_message && (
            <p className="text-xs text-destructive mt-1 break-words">{event.error_message}</p>
          )}
          {hasDetails && (
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 text-xs mt-1 px-2 -ml-2">
                <ChevronDown
                  className={`h-3 w-3 mr-1 transition-transform ${open ? 'rotate-180' : ''}`}
                />
                Detalhes
              </Button>
            </CollapsibleTrigger>
          )}
          <CollapsibleContent className="mt-2 space-y-2">
            {event.metadata && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">metadata</p>
                <pre className="text-[10px] bg-muted/50 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
                  {JSON.stringify(event.metadata, null, 2)}
                </pre>
              </div>
            )}
            {event.raw_response_snapshot && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">
                  raw_response_snapshot
                </p>
                <pre className="text-[10px] bg-muted/50 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
                  {JSON.stringify(event.raw_response_snapshot, null, 2)}
                </pre>
              </div>
            )}
            {event.error_stack && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">error_stack</p>
                <pre className="text-[10px] bg-destructive/5 text-destructive rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
                  {event.error_stack}
                </pre>
              </div>
            )}
          </CollapsibleContent>
        </div>
      </div>
    </Collapsible>
  );
}

export function CalendarOAuthDiagnosticsSheet({
  open,
  onOpenChange,
  initialCorrelationId = null,
}: Props) {
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [search, setSearch] = useState(initialCorrelationId ?? '');

  const filterCid = search.trim().length >= 8 ? search.trim() : null;

  const { data: events = [], isLoading, refetch, isFetching } = useCalendarOAuthDiagnostics({
    enabled: open,
    onlyFailed,
    correlationId: filterCid,
    refetchInterval: 5000,
  });

  const groups = useMemo(() => groupByCorrelation(events), [events]);

  const handleCopy = (groupCid: string) => {
    const group = groups.find((g) => g.correlation_id === groupCid);
    if (!group) return;
    navigator.clipboard.writeText(JSON.stringify(group, null, 2));
    toast.success('Diagnóstico copiado para a área de transferência');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle>Diagnóstico — Conexão Google Calendar</SheetTitle>
          <SheetDescription>
            Linha do tempo de cada tentativa de OAuth. Use para identificar a fase exata da falha.
          </SheetDescription>
        </SheetHeader>

        <div className="px-6 py-3 border-b space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Label htmlFor="cid-search" className="text-xs text-muted-foreground">
                Filtrar por correlation_id
              </Label>
              <Input
                id="cid-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="cole o ID completo ou primeiros 8 caracteres"
                className="h-8 text-xs font-mono"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="only-failed" className="text-xs flex items-center gap-2 cursor-pointer">
              <Switch
                id="only-failed"
                checked={onlyFailed}
                onCheckedChange={setOnlyFailed}
              />
              Somente falhas
            </Label>
            <span className="text-xs text-muted-foreground">
              {groups.length} tentativa(s)
            </span>
          </div>
        </div>

        <ScrollArea className="flex-1 px-6 py-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isLoading && groups.length === 0 && (
            <div className="text-center py-12 text-sm text-muted-foreground">
              Nenhum evento encontrado.
              <br />
              Tente conectar o Google Calendar para gerar registros.
            </div>
          )}
          <div className="space-y-4">
            {groups.map((group) => (
              <div
                key={group.correlation_id}
                className="rounded-lg border border-border bg-card overflow-hidden"
              >
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/30 border-b">
                  <div className="flex items-center gap-2 min-w-0">
                    <StatusIcon status={group.status} />
                    <span className="font-mono text-xs font-semibold">
                      {shortCid(group.correlation_id)}
                    </span>
                    <Badge
                      variant="outline"
                      className={STATUS_STYLES[group.status] || ''}
                    >
                      {group.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground truncate">
                      {format(new Date(group.started_at), "dd/MM HH:mm:ss", { locale: ptBR })}
                      {' · '}
                      {group.total_latency_ms}ms · {group.events.length} ev.
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => handleCopy(group.correlation_id)}
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copiar
                  </Button>
                </div>
                <div className="px-3 py-2">
                  {group.events.map((ev) => (
                    <EventRow key={ev.id} event={ev} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
